import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Fullscreen,
  FullscreenExit,
  VolumeUp,
  VolumeOff,
  SignalWifi4Bar,
  SignalWifi3Bar,
  SignalWifi2Bar,
  SignalWifi1Bar,
  SignalWifiOff,
  Visibility,
  Warning,
  Refresh,
} from '@mui/icons-material';
import { create as createIVSPlayer, isPlayerSupported, MediaPlayer, PlayerState, PlayerEventType, Quality } from 'amazon-ivs-player';
import EmojiReactionButtons from './EmojiReactionButtons';
import { useLiveStreamEvents } from '../../hooks/useLiveStreamEvents';
import liveStreamService, { 
  ViewerCredentials, 
  LiveStreamEmoji,
  LiveStream,
  LiveStreamStatus 
} from '../../services/live-stream-service';
import { logger } from '../../utils/logger';

/**
 * Styled container for the viewer interface
 */
const ViewerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100vh',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
}));

/**
 * Styled video container
 */
const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: 1,
  backgroundColor: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

/**
 * Styled video element
 */
const VideoPlayer = styled('video')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
}));

/**
 * Styled controls overlay
 */
const ControlsOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10,
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

/**
 * Styled info overlay
 */
const InfoOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  
  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(1),
    left: theme.spacing(1),
  },
}));

/**
 * Styled error container
 */
const ErrorContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center',
  color: 'white',
  maxWidth: 400,
  padding: theme.spacing(3),
}));

/**
 * Connection quality enum
 */
enum ConnectionQuality {
  EXCELLENT = 'excellent',
  GOOD = 'good',
  FAIR = 'fair',
  POOR = 'poor',
  OFFLINE = 'offline',
}

/**
 * Viewer state enum
 */
enum ViewerState {
  LOADING = 'loading',
  PLAYING = 'playing',
  BUFFERING = 'buffering',
  ERROR = 'error',
  ENDED = 'ended',
  CAPACITY_EXCEEDED = 'capacity_exceeded',
}

/**
 * Stream info interface
 */
interface StreamInfo {
  title: string;
  broadcasterName: string;
  viewerCount: number;
  duration: number;
}

/**
 * Props for LiveStreamViewer component
 */
interface LiveStreamViewerProps {
  /** Stream ID to watch */
  streamId: string;
  /** Playback URL for the stream */
  playbackUrl: string;
  /** Chat token for emoji reactions */
  chatToken?: string;
  /** Stream information */
  streamInfo?: StreamInfo;
  /** Whether to show ad overlays */
  showAds?: boolean;
  /** Callback when viewer leaves the stream */
  onLeave?: () => void;
  /** Callback when error occurs */
  onError?: (error: string) => void;
  /** Callback when stream ends */
  onStreamEnd?: () => void;
  /** Whether to auto-retry on connection issues */
  autoRetry?: boolean;
}

/**
 * Live stream viewer component with IVS Player integration
 */
const LiveStreamViewer: React.FC<LiveStreamViewerProps> = ({
  streamId,
  playbackUrl,
  chatToken,
  streamInfo,
  showAds = true,
  onLeave,
  onError,
  onStreamEnd,
  autoRetry = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<MediaPlayer | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const qualityCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // State
  const [viewerState, setViewerState] = useState<ViewerState>(ViewerState.LOADING);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(ConnectionQuality.GOOD);
  const [currentQuality, setCurrentQuality] = useState<Quality | null>(null);
  const [availableQualities, setAvailableQualities] = useState<Quality[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [showEndedDialog, setShowEndedDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [displayViewerCount, setDisplayViewerCount] = useState(streamInfo?.viewerCount || 0);
  const [displayDuration, setDisplayDuration] = useState(0);

  // Real-time events via native IVS Chat SDK
  const { emojis: liveEmojis, sendLocalEmoji, isConnected: isChatConnected } = useLiveStreamEvents({
    streamId,
    chatToken,
    enabled: viewerState === ViewerState.PLAYING,
  });

  /**
   * Initialize IVS Player
   */
  const initializePlayer = useCallback(async () => {
    try {
      if (!isPlayerSupported) {
        throw new Error('IVS Player is not supported in this browser');
      }

      if (!videoRef.current) {
        throw new Error('Video element not available');
      }

      const player = createIVSPlayer({
        wasmWorker: 'https://player.live-video.net/1.43.0/amazon-ivs-wasmworker.min.js',
        wasmBinary: 'https://player.live-video.net/1.43.0/amazon-ivs-wasmworker.min.wasm'
      });
      playerRef.current = player;

      // Attach player to video element
      player.attachHTMLVideoElement(videoRef.current);

      // Set up event listeners
      player.addEventListener(PlayerState.PLAYING, () => {
        setViewerState(ViewerState.PLAYING);
        setError(null);
        setRetryCount(0);
      });

      player.addEventListener(PlayerState.BUFFERING, () => {
        setViewerState(ViewerState.BUFFERING);
      });

      player.addEventListener(PlayerState.IDLE, () => {
        setViewerState(ViewerState.ENDED);
        setShowEndedDialog(true);
        if (onStreamEnd) {
          onStreamEnd();
        }
      });

      player.addEventListener(PlayerState.ENDED, () => {
        logger.info('Stream ended (ENDED state)');
        setViewerState(ViewerState.ENDED);
        setShowEndedDialog(true);
        if (onStreamEnd) {
          onStreamEnd();
        }
      });

      player.addEventListener(PlayerEventType.ERROR, (error) => {
        logger.error('IVS Player error:', error);
        setViewerState(ViewerState.ERROR);
        handlePlayerError(error);
      });

      // Quality change events
      player.addEventListener(PlayerEventType.QUALITY_CHANGED, (quality: Quality) => {
        setCurrentQuality(quality);
        updateConnectionQuality(quality);
      });

      // Load stream
      player.load(playbackUrl);
      
      // Start muted for iOS autoplay compatibility, user can unmute via button
      player.setMuted(true);
      setIsMuted(true);
      player.setVolume(1.0);
      player.play();

      // Start quality monitoring
      startQualityMonitoring();

    } catch (error) {
      logger.error('Failed to initialize player:', error);
      setError(error instanceof Error ? error.message : 'Failed to initialize player');
      setViewerState(ViewerState.ERROR);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Failed to initialize player');
      }
    }
  }, [playbackUrl, onError, onStreamEnd]);

  /**
   * Handle player errors with retry logic
   */
  const handlePlayerError = useCallback((error: any) => {
    const errorMessage = error?.message || 'Playback error occurred';
    
    if (errorMessage.includes('capacity') || errorMessage.includes('limit')) {
      setViewerState(ViewerState.CAPACITY_EXCEEDED);
      setShowCapacityDialog(true);
      return;
    }

    setError(errorMessage);
    
    if (autoRetry && retryCount < 3) {
      const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        initializePlayer();
      }, retryDelay);
    } else {
      setShowRetryDialog(true);
    }
  }, [autoRetry, retryCount, initializePlayer]);

  /**
   * Start monitoring connection quality
   */
  const startQualityMonitoring = useCallback(() => {
    qualityCheckIntervalRef.current = setInterval(() => {
      if (playerRef.current) {
        const qualities = playerRef.current.getQualities();
        setAvailableQualities(qualities);
        
        // Update connection quality based on current quality and buffering
        const currentQual = playerRef.current.getQuality();
        if (currentQual) {
          updateConnectionQuality(currentQual);
        }
      }
    }, 5000);
  }, []);

  /**
   * Update connection quality indicator
   */
  const updateConnectionQuality = useCallback((quality: Quality) => {
    if (!quality) {
      setConnectionQuality(ConnectionQuality.OFFLINE);
      return;
    }

    const bitrate = quality.bitrate || 0;
    
    if (bitrate > 2000000) {
      setConnectionQuality(ConnectionQuality.EXCELLENT);
    } else if (bitrate > 1000000) {
      setConnectionQuality(ConnectionQuality.GOOD);
    } else if (bitrate > 500000) {
      setConnectionQuality(ConnectionQuality.FAIR);
    } else if (bitrate > 0) {
      setConnectionQuality(ConnectionQuality.POOR);
    } else {
      setConnectionQuality(ConnectionQuality.OFFLINE);
    }
  }, []);

  /**
   * Toggle fullscreen mode
   */
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await videoRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      logger.error('Failed to toggle fullscreen:', error);
    }
  }, []);

  /**
   * Toggle mute/unmute
   */
  const toggleMute = useCallback(() => {
    if (playerRef.current) {
      const newMutedState = !isMuted;
      playerRef.current.setMuted(newMutedState);
      setIsMuted(newMutedState);
    }
  }, [isMuted]);

  /**
   * Retry connection
   */
  const retryConnection = useCallback(() => {
    setShowRetryDialog(false);
    setRetryCount(0);
    setError(null);
    setViewerState(ViewerState.LOADING);
    initializePlayer();
  }, [initializePlayer]);

  /**
   * Leave the stream
   */
  const leaveStream = useCallback(async () => {
    try {
      await liveStreamService.leaveStream(streamId);
      if (onLeave) {
        onLeave();
      }
    } catch (error) {
      logger.error('Failed to leave stream:', error);
      // Data fetched from backend API call fails
      if (onLeave) {
        onLeave();
      }
    }
  }, [streamId, onLeave]);

  /**
   * Handle emoji sent
   */
  const handleEmojiSent = useCallback((emoji: LiveStreamEmoji) => {
    // Show locally immediately, the backend send-emoji Lambda will broadcast via IVS Chat SendEvent
    sendLocalEmoji(emoji);
  }, [sendLocalEmoji]);

  /**
   * Handle emoji error
   */
  const handleEmojiError = useCallback((error: string) => {
    setSnackbarMessage(`Failed to send emoji: ${error}`);
  }, []);

  /**
   * Get connection quality icon
   */
  const getConnectionQualityIcon = useCallback(() => {
    switch (connectionQuality) {
      case ConnectionQuality.EXCELLENT:
        return <SignalWifi4Bar color="success" />;
      case ConnectionQuality.GOOD:
        return <SignalWifi3Bar color="primary" />;
      case ConnectionQuality.FAIR:
        return <SignalWifi2Bar color="warning" />;
      case ConnectionQuality.POOR:
        return <SignalWifi1Bar color="error" />;
      case ConnectionQuality.OFFLINE:
        return <SignalWifiOff color="error" />;
      default:
        return <SignalWifi4Bar />;
    }
  }, [connectionQuality]);

  /**
   * Format duration for display
   */
  const formatDuration = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  /**
   * Initialize player on mount
   */
  useEffect(() => {
    initializePlayer();
    
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (qualityCheckIntervalRef.current) {
        clearInterval(qualityCheckIntervalRef.current);
      }
      if (playerRef.current) {
        playerRef.current.delete();
      }
    };
  }, [initializePlayer]);

  /**
   * Handle fullscreen change events
   */
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  /**
   * Internal viewer count polling and duration tracking
   * Uses separate state that doesn't affect player initialization
   */
  useEffect(() => {
    if (viewerState !== ViewerState.PLAYING) return;
    
    const startTime = Date.now();
    const interval = setInterval(async () => {
      // Update duration locally
      setDisplayDuration(Math.floor((Date.now() - startTime) / 1000));
      
      // Poll viewer count every 30 seconds (less frequent to avoid inflating count)
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      if (elapsed % 30 < 5) {
        try {
          const streams = await liveStreamService.discoverStreams({ limit: 20, includeScheduled: false, includeTrending: false });
          const current = [...streams.liveStreams, ...streams.trendingStreams].find(s => s.streamId === streamId);
          if (current) {
            setDisplayViewerCount((current as any).currentViewers || current.totalViewers || 0);
          }
        } catch { /* ignore */ }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [viewerState, streamId]);

  return (
    <ViewerContainer>
      {/* Video Player */}
      <VideoContainer>
        <VideoPlayer
          ref={videoRef}
          playsInline
          // @ts-ignore - webkit-playsinline needed for older iOS
          webkit-playsinline="true"
          controls={false}
          muted
        />
        
        {/* Loading overlay */}
        {viewerState === ViewerState.LOADING && (
          <ErrorContainer>
            <CircularProgress size={60} />
            <Typography variant="h6" mt={2}>
              Connecting to stream...
            </Typography>
          </ErrorContainer>
        )}

        {/* Buffering overlay */}
        {viewerState === ViewerState.BUFFERING && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            sx={{ transform: 'translate(-50%, -50%)' }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {/* Error overlay */}
        {viewerState === ViewerState.ERROR && (
          <ErrorContainer>
            <Warning sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" mb={2}>
              Connection Error
            </Typography>
            <Typography variant="body2" mb={3}>
              {error || 'Unable to connect to the stream'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={retryConnection}
            >
              Retry
            </Button>
          </ErrorContainer>
        )}

        {/* Floating Emoji Reactions from all participants via IVS Chat */}
        {liveEmojis.map((emoji) => (
          <Box
            key={emoji.id}
            sx={{
              position: 'absolute',
              left: `${emoji.x}%`,
              top: `${emoji.y}%`,
              fontSize: '2rem',
              pointerEvents: 'none',
              zIndex: 22,
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              animation: 'floatUpEmoji 3s ease-out forwards',
              '@keyframes floatUpEmoji': {
                '0%': { transform: 'translateY(0) scale(1)', opacity: 1 },
                '50%': { transform: 'translateY(-60px) scale(1.3)', opacity: 0.8 },
                '100%': { transform: 'translateY(-120px) scale(0.6)', opacity: 0 },
              },
            }}
          >
            {emoji.emoji}
          </Box>
        ))}

        {/* Emoji Reaction Buttons - positioned on the right side */}
        {viewerState === ViewerState.PLAYING && (
          <Box sx={{ 
            position: 'absolute', 
            right: 16, 
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 25,
          }}>
            <EmojiReactionButtons
              streamId={streamId}
              isWatching={true}
              enabled={true}
              onEmojiSent={handleEmojiSent}
              onEmojiError={handleEmojiError}
            />
          </Box>
        )}

        {/* Tap to unmute overlay for iOS/mobile autoplay */}
        {viewerState === ViewerState.PLAYING && isMuted && (
          <Box
            onClick={toggleMute}
            sx={{
              position: 'absolute',
              bottom: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.7)',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              zIndex: 30,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.6 },
              },
            }}
          >
            <VolumeOff fontSize="small" />
            Tap to unmute
          </Box>
        )}

        {/* Stream Info Overlay */}
        <InfoOverlay>
          <Chip
            icon={<Visibility />}
            label={`${displayViewerCount} viewers`}
            color="primary"
            size="small"
          />
          <Chip
            label={formatDuration(displayDuration)}
            color="secondary"
            size="small"
          />
          <Chip
            icon={getConnectionQualityIcon()}
            label={connectionQuality}
            size="small"
          />
        </InfoOverlay>

        {/* Controls Overlay */}
        <ControlsOverlay>
          <Box display="flex" gap={1}>
            <IconButton
              color={isMuted ? "error" : "primary"}
              onClick={toggleMute}
              size="large"
            >
              {isMuted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>
            
            <IconButton
              color="primary"
              onClick={toggleFullscreen}
              size="large"
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Box>

          {streamInfo?.title && (
            <Typography variant="h6" color="white" textAlign="center">
              {streamInfo.title}
            </Typography>
          )}

          <Button
            variant="outlined"
            color="inherit"
            onClick={leaveStream}
          >
            Leave
          </Button>
        </ControlsOverlay>
      </VideoContainer>

      {/* Capacity Exceeded Dialog */}
      <Dialog open={showCapacityDialog} onClose={() => setShowCapacityDialog(false)}>
        <DialogTitle>Stream at Capacity</DialogTitle>
        <DialogContent>
          <Typography>
            This stream has reached its maximum viewer capacity. Please try again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCapacityDialog(false)}>
            OK
          </Button>
          <Button onClick={retryConnection} variant="contained">
            Try Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stream Ended Dialog */}
      <Dialog open={showEndedDialog} onClose={() => setShowEndedDialog(false)}>
        <DialogTitle>Stream Ended</DialogTitle>
        <DialogContent>
          <Typography>
            This live stream has ended. Thank you for watching!
          </Typography>
          {streamInfo && (
            <Typography color="text.secondary" mt={1}>
              Stream duration: {formatDuration(streamInfo.duration)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={leaveStream} variant="contained">
            Find Other Streams
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Dialog */}
      <Dialog open={showRetryDialog} onClose={() => setShowRetryDialog(false)}>
        <DialogTitle>Connection Failed</DialogTitle>
        <DialogContent>
          <Typography>
            Unable to connect to the stream after multiple attempts.
          </Typography>
          <Typography color="text.secondary" mt={1}>
            This could be due to network issues or the stream may have ended.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={leaveStream}>
            Leave Stream
          </Button>
          <Button onClick={retryConnection} variant="contained">
            Try Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </ViewerContainer>
  );
};

export default LiveStreamViewer;